/**
 * AddRevisionModal — create a new Delta Revision (Plan Check or Permit) under a
 * Permit, and attach it to a "commensurate" revision milestone (one whose
 * deliverable type is in POST_REVISION_TYPES and doesn't already carry a Delta
 * of this kind — see getRevisionMilestoneCandidates in services/permits.js).
 * See docs/PLAN_CHECK.md ("P6 — Delta pop-off valve", 2026-07).
 *
 * Opens via 'open-add-revision-modal' { permitId, type: 'Plan Check Revision'|'Permit Revision' }.
 * Mounted once in OutlineView.jsx.
 *
 * Milestone selection is REQUIRED — a Delta can't be created without one. If no
 * existing candidate fits, "Create a new milestone" delegates to the standard
 * AddMilestoneWizard (no changes made to it) and detects completion by watching
 * for its `[data-modal-id="AddMilestoneWizard"]` node to leave the DOM, then
 * re-queries candidates so the freshly created milestone shows up.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getRevisionMilestoneCandidates, createDeltaRevision } from '../services/permits.js'
import { alertDialog } from './Dialog.jsx'

const TYPE_LABEL = {
  'Plan Check Revision': 'Plan Check Revision',
  'Permit Revision':     'Permit Revision',
}

const modalInputStyle = {
  width: '100%', fontFamily: '"DM Mono",monospace', fontSize: 13, color: 'var(--aj-text)',
  padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4,
  background: 'var(--aj-bg-subtle)', boxSizing: 'border-box',
}

function modalLabel(text) {
  return (
    <div style={{
      fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 9,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 5,
    }}>
      {text}
    </div>
  )
}

export default function AddRevisionModal() {
  const [open, setOpen]             = useState(false)
  const [permitId, setPermitId]     = useState('')
  const [type, setType]             = useState('')
  const [reviews, setReviews]       = useState([])
  const [selectedReviewIds, setSelectedReviewIds] = useState([])
  const [candidates, setCandidates] = useState(null)   // { smart, all } | null (loading)
  const [showAll, setShowAll]       = useState(false)
  const [milestoneId, setMilestoneId] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState('')
  const [waitingForNewMs, setWaitingForNewMs] = useState(false)

  const waitingRef = useRef(false)
  waitingRef.current = waitingForNewMs

  const projectId = ctx.recordId || wf.zoomProjectId || ''

  function loadCandidates(pid, t, revs) {
    setCandidates(null)
    getRevisionMilestoneCandidates(projectId, t, revs).then(res => {
      setCandidates(res)
      setShowAll(false)
      setMilestoneId(res.smart[0]?.id || '')
    }).catch(() => setCandidates({ smart: [], all: [] }))
  }

  /* Open event */
  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.permitId || ''
      const t   = e.detail?.type || ''
      const revs = (ctx.permitReviewCache || []).filter(r => r.permitId === pid)
      setPermitId(pid)
      setType(t)
      setReviews(revs)
      setSelectedReviewIds([])
      setDescription('')
      setError('')
      setSaving(false)
      setWaitingForNewMs(false)
      loadCandidates(pid, t, revs)
      setOpen(true)
    }
    window.addEventListener('open-add-revision-modal', onOpen)
    return () => window.removeEventListener('open-add-revision-modal', onOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* While waiting on AddMilestoneWizard, poll outline-render for its modal DOM node
     disappearing (it closes itself ~800ms after a successful create). */
  useEffect(() => {
    function onRender() {
      if (!waitingRef.current) return
      if (document.querySelector('[data-modal-id="AddMilestoneWizard"]')) return
      setWaitingForNewMs(false)
      loadCandidates(permitId, type, reviews)
    }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permitId, type, reviews])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && !waitingForNewMs) close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, waitingForNewMs])

  function close() { setOpen(false) }

  function toggleReview(id) {
    setSelectedReviewIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id])
  }

  function handleCreateNewMilestone() {
    setWaitingForNewMs(true)
    window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: {} }))
  }

  async function handleSave() {
    if (!milestoneId || saving) return
    setSaving(true); setError('')
    try {
      const res = await createDeltaRevision({
        projectId, permitId, type,
        reviewIds: selectedReviewIds,
        milestoneId,
        description: description || undefined,
      })
      ctx.permitDeltaCache = undefined
      window.dispatchEvent(new Event('outline-render'))
      if (res.milestoneLinkFailed) {
        await alertDialog({
          title: 'Revision created',
          message: 'The Delta Revision was created, but linking it to the milestone failed — link it manually from the milestone\'s posting screen.',
        })
      }
      close()
    } catch (e) {
      setError(e.message || 'Failed to create revision.')
      setSaving(false)
    }
  }

  if (!open) return null

  const typeLabel = TYPE_LABEL[type] || 'Revision'
  const list = candidates ? (showAll ? candidates.all : candidates.smart) : null
  const canShowAllToggle = candidates && !showAll && candidates.all.length > candidates.smart.length

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: waitingForNewMs ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)',
        width: 460, maxWidth: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', padding: '16px 20px',
          borderBottom: '0.5px solid var(--aj-border)',
        }}>
          <div style={{
            fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12,
            letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', flex: 1,
          }}>
            Add {typeLabel}
          </div>
          <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '60vh', overflowY: 'auto' }}>
          {reviews.length > 0 && (
            <div>
              {modalLabel('Link to agency review(s) (optional)')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {reviews.map(rv => (
                  <label key={rv.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedReviewIds.includes(rv.id)} onChange={() => toggleReview(rv.id)} />
                    {(rv.agencyType ? rv.agencyType + ' — ' : '') + (rv.agency || rv.name)}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            {modalLabel('Revision milestone (required)')}
            {candidates === null && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '8px 0' }}>
                Loading milestones…
              </div>
            )}
            {candidates !== null && list.length === 0 && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)', padding: '4px 0 8px' }}>
                No available revision milestone found{showAll ? '' : ' for this permit\'s track'}.
              </div>
            )}
            {candidates !== null && list.length > 0 && (
              <select value={milestoneId} onChange={e => setMilestoneId(e.target.value)} style={{ ...modalInputStyle, fontFamily: '"DM Sans",sans-serif' }}>
                <option value="">— select a milestone —</option>
                {list.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            {canShowAllToggle && (
              <button onClick={() => setShowAll(true)} style={{ background: 'none', border: 'none', color: '#2563eb', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: 'pointer', padding: '6px 0 0' }}>
                Show all revision milestones
              </button>
            )}
            <button onClick={handleCreateNewMilestone} style={{ background: 'none', border: 'none', color: '#2563eb', fontFamily: '"DM Sans",sans-serif', fontSize: 12, cursor: 'pointer', padding: '6px 0 0', display: 'block' }}>
              + Create a new milestone instead
            </button>
          </div>

          <div>
            {modalLabel('Description (optional)')}
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              rows={3}
              style={{ ...modalInputStyle, fontFamily: '"DM Sans",sans-serif', resize: 'vertical' }}
            />
          </div>

          {error && <div style={{ color: '#dc2626', fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button
            onClick={close}
            style={{ background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !milestoneId}
            style={{ background: '#07272D', border: 'none', borderRadius: 4, padding: '8px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, color: 'white', cursor: (saving || !milestoneId) ? 'default' : 'pointer', opacity: (saving || !milestoneId) ? 0.5 : 1 }}
          >
            {saving ? 'Creating…' : 'Create revision'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
